// useNudgePulse — Phase 2.5e: post-presentation "stay + nudge" polish.
//
// After the agent finishes speaking (concierge:audio_done), the last scene
// stays on stage. NUDGE_DELAY_MS later, the orb does ONE soft pulse cycle
// (≈ PULSE_DURATION_MS) using the same boosted scale + glow as the speaking
// state — a quiet "what next?" cue that doesn't drag the visitor back to
// a generic landing.
//
// Cancellations (timer reset, no pulse fires):
//   - concierge:audio_started — agent started speaking again
//   - concierge:interrupt     — barge-in
//   - concierge:storyboard    — new scene set arriving
//
// The hook returns a boolean. The Orb merges it with `speaking` so the same
// transform path drives both states.

import { useEffect, useState } from 'react';

const NUDGE_DELAY_MS    = 12000;
const PULSE_DURATION_MS = 600;

export function useNudgePulse(): boolean {
  const [nudgeActive, setNudgeActive] = useState(false);
  useEffect(() => {
    let pulseStart:  ReturnType<typeof setTimeout> | null = null;
    let pulseEnd:    ReturnType<typeof setTimeout> | null = null;
    const clearAll = () => {
      if (pulseStart) { clearTimeout(pulseStart); pulseStart = null; }
      if (pulseEnd)   { clearTimeout(pulseEnd);   pulseEnd   = null; }
      setNudgeActive(false);
    };
    const onAudioDone = () => {
      clearAll();
      pulseStart = setTimeout(() => {
        setNudgeActive(true);
        pulseEnd = setTimeout(() => setNudgeActive(false), PULSE_DURATION_MS);
      }, NUDGE_DELAY_MS);
    };
    window.addEventListener('concierge:audio_done',    onAudioDone);
    window.addEventListener('concierge:audio_started', clearAll);
    window.addEventListener('concierge:interrupt',     clearAll);
    window.addEventListener('concierge:storyboard',    clearAll);
    return () => {
      window.removeEventListener('concierge:audio_done',    onAudioDone);
      window.removeEventListener('concierge:audio_started', clearAll);
      window.removeEventListener('concierge:interrupt',     clearAll);
      window.removeEventListener('concierge:storyboard',    clearAll);
      if (pulseStart) clearTimeout(pulseStart);
      if (pulseEnd)   clearTimeout(pulseEnd);
    };
  }, []);
  return nudgeActive;
}
